<template>
  <div class="attributes-container" v-if="layer">

    <div class="header-bar">
      <span class="layer-title">{{ layer.name }}</span>

      <div class="header-tools">
        <button class="icon-btn action-btn" @click="applyFilters" :disabled="isLoading"
          :class="{ 'filter-active': hasActiveFilter }" title="Buscar">
          <font-awesome-icon icon="search" />
        </button>

        <!-- DOWNLOAD BUTTON -->
        <button class="icon-btn action-btn download-btn" @click="openDownloadModal" :disabled="isLoading" title="Descargar datos">
          <font-awesome-icon icon="download" />
        </button>

        <!-- DOWNLOAD MODAL COMPONENT -->
        <DownloadModal />

        <!-- NEW: Filter by map extent -->
        <button class="icon-btn action-btn spatial-btn" @click="applySpatialFilter" :disabled="isLoading"
          title="Filtrar por vista actual del mapa">
          <span class="fa-stack fa-sm">
            <font-awesome-icon icon="map" class="fa-stack-1x" />
            <font-awesome-icon icon="filter" class="fa-stack-1x fa-inverse"
              style="font-size: 0.5em; margin-top: 4px; margin-left: 4px;" />
          </span>
        </button>

        <!-- NEW: Clear spatial filter -->
        <button class="icon-btn action-btn clear-spatial-btn" @click="clearSpatialFilter"
          :disabled="!hasSpatialFilter || isLoading"
          :class="{ 'spatial-active': hasSpatialFilter }"
          title="Limpiar filtro espacial">
          <font-awesome-icon icon="times-circle" />
        </button>

        <button class="icon-btn nav-btn" @click="prevPage" :disabled="firstPage || isLoading" title="Anterior">
          <font-awesome-icon icon="chevron-left" />
        </button>

        <button class="icon-btn nav-btn" @click="nextPage" :disabled="lastPage || isLoading" title="Siguiente">
          <font-awesome-icon icon="chevron-right" />
        </button>

        <button class="icon-btn close-btn" @click="closeTable" title="Cerrar">
          <font-awesome-icon icon="times" />
        </button>
      </div>
    </div>

    <div class="table-wrapper">
      <table class="attr-table">

        <colgroup>
          <col v-for="h in headers" :key="h" style="width:250px;" />
        </colgroup>

        <thead>
          <tr>
            <th v-for="h in headers" :key="'h' + h" class="col-header">{{ h }}</th>
          </tr>

          <tr>
            <th v-for="h in headers" :key="'f' + h" class="filter-cell">
              <input class="filter-input" type="text"
                :value="columnFilters[h] || ''"
                @input="onFilterChange(h, $event.target.value)"
                placeholder="" />
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(row, i) in attributes" :key="i" :class="{ 'odd-row': i % 2 === 1 }" @click="zoomToFeature(row)" class="clickable-row" title="Clic para centrar en el mapa">

            <td v-for="h in headers" :key="'d' + h" class="data-cell">
              {{ row[h] }}
            </td>
          </tr>

          <tr v-if="!attributes.length">
            <td :colspan="headers.length" class="no-data">Sin datos disponibles</td>
          </tr>
        </tbody>

      </table>
    </div>

  </div>
</template>

<script>
import { attributesTableService } from "@/services/layers/attributesTable";
import { mapService } from "@/services/map/mapService";
import { computeAggregateBbox, padExtent } from "@/services/helpers/bboxHelper";
import DownloadModal from "./DownloadModal.vue";

export default {
  components: {
    DownloadModal
  },
  computed: {
    layer() {
      return this.$store.getters["attributeTable/layer"];
    },
    attributes() {
      return this.$store.getters["attributeTable/attributes"];
    },
    totalFeatures() {
      return this.$store.getters["attributeTable/totalFeatures"];
    },
    spatialFilter() {
      return this.$store.getters["attributeTable/spatialFilter"];
    },
    hasSpatialFilter() {
      return !!this.$store.getters["attributeTable/spatialFilter"];
    },
    columnFilters() {
      return this.$store.getters["attributeTable/columnFilters"];
    },
    headers() {
      if (!this.attributes.length) return [];
      return Object.keys(this.attributes[0]).filter(key => key !== 'bbox');
    },
    firstPage() {
      return this.offset === 0;
    },
    lastPage() {
      if (this.totalFeatures) {
        return this.offset + 10 >= this.totalFeatures;
      }
      return this.attributes.length < 10;
    },
    hasActiveFilter() {
      const p = this.appliedFilterParams;
      if (!p) return false;
      return !!(p.cqlFilter || p.layerDefs);
    },
  },
  watch: {
    layer(newLayer) {
      if (!newLayer) return;
      // Re-apply persisted filters to WMS when the table is (re)opened
      const filterParams = attributesTableService.buildWMSFilterParams(
        newLayer,
        this.$store.getters["attributeTable/columnFilters"],
        this.$store.getters["attributeTable/spatialFilter"]
      );
      mapService.updateLayerFilter(newLayer.id, filterParams);
      this.appliedFilterParams = filterParams;
    }
  },
  data() {
    return {
      offset: 0,
      totalPages: 0,
      isLoading: false,
      appliedFilterParams: null
    };
  },
  methods: {
    closeTable() {
      this.$store.dispatch("attributeTable/closeTable");
    },
    async applySpatialFilter() {
      if (this.isLoading) return;
      this.isLoading = true;
      const map = mapService.get();
      if (!map) {
        this.isLoading = false;
        return;
      }
      const extent = map.getView().calculateExtent(map.getSize());
      this.$store.dispatch("attributeTable/setSpatialFilter", extent);
      this.offset = 0;
      await attributesTableService.updateTable(this.layer, this.offset, this.columnFilters, extent);
      // Sync spatial + column filters to WMS layer
      const filterParams = attributesTableService.buildWMSFilterParams(
        this.layer, this.columnFilters, extent
      );
      mapService.updateLayerFilter(this.layer.id, filterParams);
      this.appliedFilterParams = filterParams;
      this.isLoading = false;
    },

    async clearSpatialFilter() {
      if (this.isLoading) return;
      this.isLoading = true;
      this.$store.dispatch("attributeTable/clearSpatialFilter");
      this.offset = 0;
      await attributesTableService.updateTable(this.layer, this.offset, this.columnFilters, null);
      // Re-apply column-only filter (spatial cleared)
      const filterParams = attributesTableService.buildWMSFilterParams(
        this.layer, this.columnFilters, null
      );
      mapService.updateLayerFilter(this.layer.id, filterParams);
      this.appliedFilterParams = filterParams;
      this.isLoading = false;
    },

    async applyFilters() {
      if (this.isLoading) return;
      this.isLoading = true;
      this.offset = 0;
      await attributesTableService.updateTable(this.layer, this.offset, this.columnFilters, this.spatialFilter);
      // Sync column + spatial filters to WMS layer
      const filterParams = attributesTableService.buildWMSFilterParams(
        this.layer, this.columnFilters, this.spatialFilter
      );
      mapService.updateLayerFilter(this.layer.id, filterParams);
      this.appliedFilterParams = filterParams;

      // ── PHASE 2: Zoom map to aggregate bbox of visible rows ──
      const currentAttributes = this.$store.getters["attributeTable/attributes"];
      if (currentAttributes.length > 0) {
        const { extent, missingCount, totalCount } = computeAggregateBbox(currentAttributes);

        if (extent) {
          const paddedExtent = padExtent(extent, {
            paddingFactor: 0.1,
            minBuffer: 100
          });
          mapService.zoomToExtent(paddedExtent);

          if (missingCount > 0) {
            this.$store.dispatch("toast/show", {
              message: `${missingCount} de ${totalCount} registros sin datos de ubicación`,
              type: "warning"
            });
          }
        } else {
          this.$store.dispatch("toast/show", {
            message: "No se puede ajustar el mapa: sin datos de ubicación disponibles",
            type: "warning"
          });
        }
      } else {
        this.$store.dispatch("toast/show", {
          message: "No se encontraron registros con este filtro",
          type: "warning"
        });
      }
      // ── END PHASE 2 ──

      this.isLoading = false;
    },
    async prevPage() {
      if (this.firstPage || this.isLoading) return;
      this.isLoading = true;
      this.offset -= 10;
      if (this.offset < 0) this.offset = 0;
      await attributesTableService.updateTable(this.layer, this.offset, this.columnFilters, this.spatialFilter);
      this.isLoading = false;
    },
    async nextPage() {
      if (this.lastPage || this.isLoading) return;
      this.isLoading = true;
      this.offset += 10;
      await attributesTableService.updateTable(this.layer, this.offset, this.columnFilters, this.spatialFilter);
      this.isLoading = false;
    },
    onFilterChange(key, val) {
      this.$store.dispatch("attributeTable/setColumnFilter", { key, val });
    },
    zoomToFeature(row) {
      console.log("Zoom:", row);
      if (row.bbox) {
        mapService.zoomToExtent(row.bbox);
      } else {
        console.warn("No bbox available for this feature");
      }
    },
    openDownloadModal() {
      this.$modal.show("DownloadModal");
    }
  }
};
</script>

<style scoped>
.attributes-container {
  position: absolute;
  bottom: 0;
  left: 0;
  max-height: 280px;
  overflow: hidden;
  width: 100%;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  border: none;
  border-radius: 10px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  background: white;
  z-index: 10000;
  /* Force above map controls */
}

.header-bar {
  background: linear-gradient(135deg, #0f4c75 0%, #3282b8 100%);
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.layer-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: 0.5px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.header-tools {
  display: flex;
  gap: 8px;
}

.icon-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 0.85rem;
  color: white;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
}

.icon-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.35);
  transform: translateY(-1px);
}

.icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  background: rgba(255, 255, 255, 0.1);
}

.close-btn {
  background: rgba(229, 57, 53, 0.8);
}

.close-btn:hover {
  background: #E53935;
}

.download-btn {
  background: rgba(30, 160, 240, 0.35);
}

.download-btn:hover:not(:disabled) {
  background: rgba(30, 160, 240, 0.5);
}

.table-wrapper {
  max-height: 220px;
  overflow: auto;
  width: 100%;
}

.attr-table {
  min-width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  /* Allows sticky to work well with borders if handled right, or keep collapse */
  border-collapse: collapse;
  border-spacing: 0;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 5;
  background: #ffffff;
  border-right: 1px solid #e1e8ed !important;
  text-align: center;
  box-shadow: 4px 0 6px -4px rgba(0, 0, 0, 0.1);
  width: 48px !important;
  min-width: 48px !important;
  max-width: 48px !important;
}

.col-header {
  background: #f8fafc;
  color: #1e293b;
  padding: 12px 10px;
  border-bottom: 2px solid #e2e8f0;
  border-right: 1px solid #f1f5f9;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  position: sticky;
  top: 0;
  z-index: 2;
  width: 250px;
  min-width: 250px;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-header.sticky-col {
  background: #f8fafc !important;
  top: 0;
  z-index: 6;
  width: 48px !important;
  min-width: 48px !important;
  max-width: 48px !important;
  border-right: 2px solid #e2e8f0 !important;
}

.filter-cell {
  background: #ffffff;
  padding: 6px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 41px;
  /* Adjusted for taller header */
  z-index: 2;
}

.filter-cell.sticky-col {
  background: #ffffff !important;
  top: 41px;
  z-index: 6;
}

.filter-input {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.8rem;
  transition: border-color 0.2s;
}

.filter-input:focus {
  outline: none;
  border-color: #3282b8;
  box-shadow: 0 0 0 2px rgba(50, 130, 184, 0.1);
}

.attr-table tbody tr {
  background-color: white;
  transition: background-color 0.15s;
}

.attr-table tbody tr:hover {
  background-color: #f1f7fc !important;
}

.clickable-row {
  cursor: pointer;
}

.clickable-row:active {
  background-color: #dbeafe !important;
}

.data-cell {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  border-right: 1px solid #f8fafc;
  color: #334155;
  font-size: 0.85rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  width: 250px;
  min-width: 250px;
  max-width: 250px;
}

.odd-row {
  background: #f8fafc !important;
}



.no-data {
  text-align: center;
  padding: 30px;
  font-style: italic;
  color: #64748b;
  background: white;
}

.spatial-btn .fa-stack {
  width: 1em;
  height: 1em;
  line-height: 1em;
}

.clear-spatial-btn.spatial-active {
  background: rgba(217, 119, 6, 0.8);
}

.clear-spatial-btn.spatial-active:hover {
  background: #d97706;
}

.action-btn.filter-active {
  background: rgba(50, 200, 100, 0.65);
}

.action-btn.filter-active:hover:not(:disabled) {
  background: rgba(40, 180, 85, 0.85);
}
</style>